import { useState, useEffect, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Call, Device } from '@twilio/voice-sdk'
import {
  Phone01Icon,
  PhoneHangUpIcon,
  Microphone01Icon,
  MicrophoneOff01Icon,
} from '@untitledui-icons/react/line'
import { Button } from '@/components/base/buttons'
import { LoadingSpinner } from '@/components/common'
import { useIdentity } from '@/hooks/api'
import { useTwilioToken } from '@/hooks/api/useTwilioToken'
import { cn } from '@/lib/utils'

/**
 * Call status states
 */
type CallStatus =
  | 'initializing'
  | 'ready'
  | 'connecting'
  | 'ringing'
  | 'connected'
  | 'disconnected'
  | 'error'

interface VoiceCallProps {
  assistantId: number
  /** E.164 number of the assistant; sent as `To` and `Called` so the voice LB routes browser calls like PSTN calls */
  assistantPhoneNumber?: string | null
  onClose?: () => void
  className?: string
}

/**
 * DTMF Keypad button component
 */
function KeypadButton({
  digit,
  onPress,
  disabled,
}: {
  digit: string
  onPress: (digit: string) => void
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      onClick={() => onPress(digit)}
      disabled={disabled}
      className={cn(
        'flex h-14 w-14 items-center justify-center rounded-xl border text-lg font-semibold transition-all',
        'border-gray-200 bg-white hover:bg-gray-50 active:bg-gray-100',
        'dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700 dark:active:bg-gray-600',
        'text-gray-900 dark:text-gray-100',
        'disabled:cursor-not-allowed disabled:opacity-40'
      )}
    >
      {digit}
    </button>
  )
}

/**
 * VoiceCall component - Enables browser-based voice calls to assistants using Twilio Voice SDK
 */
export function VoiceCall({ assistantId, assistantPhoneNumber, onClose, className }: VoiceCallProps) {
  const { t } = useTranslation()
  const { user } = useIdentity()
  const { mutateAsync: fetchTwilioToken } = useTwilioToken()

  // Device and call state
  const [callStatus, setCallStatus] = useState<CallStatus>('initializing')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isMuted, setIsMuted] = useState(false)
  const [timer, setTimer] = useState(0)

  // Refs for Twilio objects
  const deviceRef = useRef<Device | null>(null)
  const currentCallRef = useRef<Call | null>(null)
  const timerIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const isInitializedRef = useRef(false)

  /**
   * Format seconds to HH:MM:SS
   */
  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const remainingSeconds = seconds % 60
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`
  }

  /**
   * Fetch a new Twilio token
   */
  const fetchToken = useCallback(async (): Promise<string | null> => {
    if (!user?.id) return null

    try {
      const result = await fetchTwilioToken({
        identity: String(user.id),
        assistantId,
      })
      return result.token
    } catch (error) {
      console.error('Error fetching Twilio token:', error)
      setCallStatus('error')
      setErrorMessage(t('components.voice_call.error_fetch_token'))
      return null
    }
  }, [user?.id, assistantId, fetchTwilioToken, t])

  /**
   * Update the device token when it expires
   */
  const updateToken = useCallback(async () => {
    const newToken = await fetchToken()
    if (newToken && deviceRef.current) {
      await deviceRef.current.updateToken(newToken)
    }
  }, [fetchToken])

  /**
   * Initialize the Twilio Device
   */
  const setupDevice = useCallback(async () => {
    const token = await fetchToken()
    if (!token) return

    // Destroy existing device if any
    if (deviceRef.current) {
      deviceRef.current.destroy()
    }

    // Create new device
    deviceRef.current = new Device(token, {
      codecPreferences: ['opus', 'pcmu'] as Call.Codec[],
      logLevel: 0,
      edge: 'dublin',
    })

    // Set up event handlers
    deviceRef.current.on('registered', () => {
      setCallStatus('ready')
      setErrorMessage(null)
    })

    deviceRef.current.on('error', async (error) => {
      console.error('Twilio Device error:', error)
      setCallStatus('error')
      setErrorMessage(error.message || t('components.voice_call.error_device'))

      // Handle token expiration errors
      if (error.code === 31204 || error.code === 20101) {
        await updateToken()
      }
    })

    deviceRef.current.on('tokenWillExpire', updateToken)

    // Register the device
    try {
      await deviceRef.current.register()
    } catch (error) {
      console.error('Failed to register Twilio device:', error)
      setCallStatus('error')
      setErrorMessage(t('components.voice_call.error_connect'))
    }
  }, [fetchToken, updateToken, t])

  /**
   * Initialize device on mount - only runs once when user is available
   */
  useEffect(() => {
    if (user?.id && !isInitializedRef.current) {
      isInitializedRef.current = true
      setupDevice()
    }

    return () => {
      // Cleanup on unmount
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current)
      }
      if (deviceRef.current) {
        deviceRef.current.destroy()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id])

  /**
   * Start the call timer when connected
   */
  useEffect(() => {
    if (callStatus === 'connected') {
      setTimer(0)
      timerIntervalRef.current = setInterval(() => {
        setTimer((prev) => prev + 1)
      }, 1000)
    } else {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current)
        timerIntervalRef.current = null
      }
    }

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current)
      }
    }
  }, [callStatus])

  /**
   * Handle DTMF digit press
   */
  const handleDtmf = useCallback((digit: string) => {
    if (currentCallRef.current && currentCallRef.current.status() === 'open') {
      currentCallRef.current.sendDigits(digit)
    }
  }, [])

  /**
   * Start a call
   */
  const handleCall = useCallback(async () => {
    if (!deviceRef.current || callStatus !== 'ready') {
      return
    }

    try {
      setCallStatus('connecting')
      setErrorMessage(null)

      // Twilio merges custom connect params over its own webhook params by key
      // (verified via the Request Inspector). `Called` is empty for Voice SDK
      // calls, and the voice LB keys per-number overrides on it, so sending the
      // assistant's number as `Called` makes overrides apply to browser test
      // calls too. Only a real number is sent: the backend uses `Called` as the
      // dialed number and transfer caller-id, so a placeholder must not leak in.
      // `To` always carries a value (`client:<id>` fallback) for LB routing.
      // The backend resolves the assistant from the token's Digits either way.
      const params: Record<string, string> = { To: assistantPhoneNumber || `client:${assistantId}` }
      if (assistantPhoneNumber) {
        params.Called = assistantPhoneNumber
      }
      const call = await deviceRef.current.connect({ params })
      currentCallRef.current = call

      call.on('accept', () => setCallStatus('connected'))
      call.on('ringing', () => setCallStatus('ringing'))
      call.on('disconnect', () => {
        setCallStatus('ready')
        setIsMuted(false)
      })
      call.on('cancel', () => {
        setCallStatus('ready')
        setIsMuted(false)
      })
      call.on('error', (error) => {
        console.error('Call error:', error)
        setCallStatus('error')
        setErrorMessage(error.message || t('components.voice_call.error_call_failed'))
      })
    } catch (error) {
      console.error('Error starting call:', error)
      setCallStatus('error')
      setErrorMessage(error instanceof Error ? error.message : t('components.voice_call.error_start_call'))
    }
  }, [callStatus, t, assistantId, assistantPhoneNumber])

  /**
   * End the current call
   */
  const handleEndCall = useCallback(() => {
    if (deviceRef.current) {
      deviceRef.current.disconnectAll()
    }
    setCallStatus('ready')
    setIsMuted(false)
  }, [])

  /**
   * Toggle mute
   */
  const handleToggleMute = useCallback(() => {
    if (currentCallRef.current) {
      const newMuteState = !isMuted
      currentCallRef.current.mute(newMuteState)
      setIsMuted(newMuteState)
    }
  }, [isMuted])

  /**
   * Retry device setup after error
   */
  const handleRetry = useCallback(() => {
    setCallStatus('initializing')
    setErrorMessage(null)
    isInitializedRef.current = false
    setupDevice()
  }, [setupDevice])

  // DTMF keypad digits
  const keypadDigits = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#']

  const isInCall = callStatus === 'connecting' || callStatus === 'ringing' || callStatus === 'connected'

  return (
    <div
      className={cn(
        'flex flex-col items-center rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800',
        className
      )}
    >
      {/* Timer / Status display */}
      <div className="mb-6 text-center">
        {callStatus === 'connected' ? (
          <div className="text-4xl font-mono font-semibold text-gray-900 dark:text-gray-100">
            {formatTime(timer)}
          </div>
        ) : (
          <div className="text-lg font-medium text-gray-600 dark:text-gray-400">
            {callStatus === 'initializing' && t('components.voice_call.status_initializing')}
            {callStatus === 'ready' && t('components.voice_call.status_ready')}
            {callStatus === 'connecting' && t('components.voice_call.status_connecting')}
            {callStatus === 'ringing' && t('components.voice_call.status_ringing')}
            {callStatus === 'disconnected' && t('components.voice_call.status_call_ended')}
            {callStatus === 'error' && t('components.voice_call.status_error')}
          </div>
        )}

        {/* Error message */}
        {errorMessage && (
          <p className="mt-2 text-sm text-red-500 dark:text-red-400">
            {errorMessage}
          </p>
        )}
      </div>

      {/* DTMF Keypad */}
      <div className="mb-6 grid grid-cols-3 gap-3">
        {keypadDigits.map((digit) => (
          <KeypadButton
            key={digit}
            digit={digit}
            onPress={handleDtmf}
            disabled={callStatus !== 'connected'}
          />
        ))}
      </div>

      {/* Call controls */}
      <div className="flex items-center gap-4">
        {/* Mute button (only show when in call) */}
        {isInCall && callStatus === 'connected' && (
          <Button
            color="secondary"
            size="lg"
            onPress={handleToggleMute}
            className={cn(
              'h-14 w-14 rounded-full p-0',
              isMuted && 'bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400'
            )}
            iconLeading={isMuted ? MicrophoneOff01Icon : Microphone01Icon}
          />
        )}

        {/* Main call button */}
        {callStatus === 'initializing' && (
          <div className="flex h-14 w-14 items-center justify-center">
            <LoadingSpinner size="md" />
          </div>
        )}

        {callStatus === 'ready' && (
          <button
            type="button"
            onClick={handleCall}
            className="flex h-14 w-14 items-center justify-center rounded-full bg-green-500 text-white transition-colors hover:bg-green-600 active:bg-green-700"
          >
            <Phone01Icon className="h-6 w-6" />
          </button>
        )}

        {(callStatus === 'connecting' || callStatus === 'ringing') && (
          <button
            type="button"
            onClick={handleEndCall}
            className="flex h-14 w-14 items-center justify-center rounded-full bg-red-500 text-white transition-colors hover:bg-red-600 active:bg-red-700"
          >
            <PhoneHangUpIcon className="h-6 w-6" />
          </button>
        )}

        {callStatus === 'connected' && (
          <button
            type="button"
            onClick={handleEndCall}
            className="flex h-14 w-14 items-center justify-center rounded-full bg-red-500 text-white transition-colors hover:bg-red-600 active:bg-red-700"
          >
            <PhoneHangUpIcon className="h-6 w-6" />
          </button>
        )}

        {callStatus === 'error' && (
          <Button color="primary" onPress={handleRetry}>
            {t('components.voice_call.retry')}
          </Button>
        )}

        {callStatus === 'disconnected' && (
          <button
            type="button"
            onClick={() => setCallStatus('ready')}
            className="flex h-14 w-14 items-center justify-center rounded-full bg-green-500 text-white transition-colors hover:bg-green-600 active:bg-green-700"
          >
            <Phone01Icon className="h-6 w-6" />
          </button>
        )}
      </div>

      {/* Close button for modal usage */}
      {onClose && (
        <Button
          color="tertiary"
          size="sm"
          onPress={onClose}
          className="mt-6 text-gray-500"
        >
          {t('components.voice_call.close')}
        </Button>
      )}
    </div>
  )
}

export default VoiceCall
